import { subtenantGuard } from './core/guards/subtenant-guard';
import { Routes } from '@angular/router';
import { LoginScreen } from './screens/login-screen/login-screen';
import { authGuard } from './core/guards/auth-guard';
import { featureFlagGuard } from './core/guards/feature-flag-guard';
import { TestChatScreen } from './screens/test-chat-screen/test-chat-screen';
import { AuthenticatedLayout } from './ui/authenticated-layout/authenticated-layout';
import { tenant } from '../tenants/tenant';
import { translatedTitle } from './core/utils/translated-title.util';
import { allTicketsTitle } from './core/services/ticket-access-service';

const CHAT_TYPES = ['standard', 'standalone'] as const;

function chatRoutes(suffix: string, data: Record<string, unknown> = {}): Routes {
  return [
    ...CHAT_TYPES.map(type => ({
      path: `chat/${type}/${suffix}`,
      component: TestChatScreen,
      title: 'Chat',
      data: { ...data, type },
    })),
    {
      path: `chat/:type/${suffix}`,
      redirectTo: `chat/${CHAT_TYPES[0]}/${suffix}`,
      pathMatch: 'full',
    },
  ];
}

export const routes: Routes = [
  ...chatRoutes('public'),
  //temporary chats with subtenants for testing
  ...chatRoutes('public1', { subTenantId: 'nexa-1' }),
  ...chatRoutes('public2', { subTenantId: 'nexa-2' }),
  {
    path: 'error-page',
    loadComponent: () =>
      import('./screens/error-screen/error-screen').then(c => c.ErrorScreen),
    title: 'Errore',
  },
  ...(tenant.samlEnabled
    ? [
      {
        path: 'login-admin',
        component: LoginScreen,
        title: 'Login Admin',
      },
      {
        path: 'logout-handler',
        loadComponent: () =>
          import('./screens/logout-handler-screen/logout-handler-screen').then(
            c => c.LogoutHandlerScreen
          ),
        title: 'Logout',
      },
    ]
    : [
      {
        path: 'login',
        component: LoginScreen,
        title: 'Login',
      },
      {
        path: 'accedi',
        component: LoginScreen,
        title: 'Accedi',
      },
    ]),
  {
    path: 'select-subtenant',
    loadComponent: () => import('./features/select-subtenant/select-subtenant.component').then(c => c.SelectSubtenantComponent),
    canActivate: [authGuard]
  },
  {
    path: '',
    component: AuthenticatedLayout,
    canActivate: [authGuard, subtenantGuard],
    runGuardsAndResolvers: 'always',
    children: [
      ...chatRoutes('private'),
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        loadComponent: () =>
          import('./screens/home-screen/home-screen').then(c => c.HomeScreen),
        title: 'Home',
      },
      {
        path: 'documenti',
        loadComponent: () =>
          import('./screens/documents-screen/documents-screen').then(c => c.DocumentsScreen),
        canActivate: [featureFlagGuard],
        title: 'Documenti',
        data: {
          featureName: 'document',
          breadcrumb: 'NAV.DOCUMENTS',
        },
      },
      {
        path: 'valutazioni',
        loadComponent: () =>
          import('./screens/feedback-screen/feedback-screen').then(c => c.FeedbackScreen),
        canActivate: [featureFlagGuard],
        title: 'Valutazioni',
        data: {
          featureName: 'feedback',
          breadcrumb: 'NAV.FEEDBACK',
        },
      },
      {
        path: 'conversazioni',
        loadComponent: () =>
          import('./screens/conversations-screen/conversations-screen').then(c => c.ConversationsScreen),
        canActivate: [featureFlagGuard],
        title: 'Conversazioni',
        data: {
          featureName: 'conversations',
          breadcrumb: 'NAV.CONVERSATIONS',
        },
      },
      {
        path: 'statistiche',
        loadComponent: () =>
          import('./screens/statistics-screen/statistics-screen').then(c => c.StatisticsScreen),
        canActivate: [featureFlagGuard],
        title: 'Statistiche',
        data: {
          featureName: 'statistics',
          breadcrumb: 'NAV.STATISTICS',
        },
      },
      {
        path: 'impostazioni',
        loadComponent: () =>
          import('./screens/impostazioni-screen/impostazioni-screen').then(c => c.ImpostazioniScreen),
        canActivate: [featureFlagGuard],
        title: 'Impostazioni',
        data: {
          featureName: 'settings',
          requiresAdmin: true,
          breadcrumb: 'NAV.SETTINGS',
        },
      },
      {
        path: 'permessi',
        loadComponent: () =>
          import('./screens/permissions-screen/permissions-screen').then(c => c.PermessiScreen),
        canActivate: [featureFlagGuard],
        title: 'Permessi',
        data: {
          featureName: 'permissions',
          requiresAdmin: true,
          breadcrumb: 'NAV.PERMISSIONS',
        },
      },
      {
        path: 'chunks',
        loadComponent: () =>
          import('./screens/chunks-search-screen/chunks-search-screen').then(c => c.ChunksSearchScreen),
        canActivate: [featureFlagGuard],
        title: 'Ricerca Chunks',
        data: {
          featureName: 'chunk',
          breadcrumb: 'NAV.CHUNKS',
        },
      },
      {
        path: 'ticketing',
        // canActivate: [featureFlagGuard],
        title: translatedTitle('TICKETING.TITLE'),
        data: {
          featureName: 'ticketing',
          breadcrumb: 'NAV.TICKETING',
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./screens/ticketing-screen/ticketing-screen').then(c => c.TicketingScreen),
            children: [
              {
                path: '',
                redirectTo: 'my-tickets',
                pathMatch: 'full',
              },
              {
                path: 'my-tickets',
                loadComponent: () =>
                  import('./screens/ticketing-screen/pages/my-tickets-page/my-tickets-page').then(
                    c => c.MyTicketsPage
                  ),
                title: translatedTitle('TICKETING.TABS.MINE'),
              },
              {
                path: 'unassigned',
                loadComponent: () =>
                  import(
                    './screens/ticketing-screen/pages/unassigned-tickets-page/unassigned-tickets-page'
                  ).then(c => c.UnassignedTicketsPage),
                title: translatedTitle('TICKETING.TABS.UNASSIGNED'),
              },
              {
                path: 'all',
                loadComponent: () =>
                  import('./screens/ticketing-screen/pages/all-tickets-page/all-tickets-page').then(
                    c => c.AllTicketsPage
                  ),
                title: allTicketsTitle,
              },
            ],
          },
          {
            path: ':ticketId',
            data: {
              breadcrumb: ':ticketId',
            },
            loadComponent: () =>
              import('./screens/ticket-detail-screen/ticket-detail-screen').then(
                c => c.TicketDetailScreen
              ),
            title: translatedTitle('TICKETING.DETAIL.TITLE'),
          },
        ],
      },
      {
        path: 'ingestion-link',
        loadComponent: () =>
          import('./screens/ingestion-link-screen/ingestion-link-screen').then(c => c.IngestionLinkScreen),
        canActivate: [featureFlagGuard],
        title: 'Ingestion Link',
        data: {
          featureName: 'ingestion-link',
          breadcrumb: 'NAV.INGESTION_LINK',
        },
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
